import {
	DOCUMENT_CATEGORIES,
	DOCUMENT_LANGUAGES,
	type Document,
	type DocumentCategory,
	type DocumentLanguage,
} from "@/types/document/document";

export type DocumentLanguageSectionKey = DocumentLanguage | "unassigned";

export type DocumentCategoryGroup = {
	category: DocumentCategory;
	documents: Document[];
};

export type DocumentLanguageSection = {
	key: DocumentLanguageSectionKey;
	language: DocumentLanguage | null;
	categories: DocumentCategoryGroup[];
};

const isDocumentLanguage = (value: unknown): value is DocumentLanguage =>
	typeof value === "string" &&
	(DOCUMENT_LANGUAGES as readonly string[]).includes(value);

const getLanguageSectionOrder = (
	preferredLanguage: DocumentLanguage
): DocumentLanguageSectionKey[] => {
	const otherLanguage: DocumentLanguage =
		preferredLanguage === "en" ? "nb-NO" : "en";
	return [preferredLanguage, otherLanguage, "unassigned"];
};

const groupByCategory = (documents: Document[]): DocumentCategoryGroup[] =>
	DOCUMENT_CATEGORIES.map((category) => ({
		category,
		documents: documents.filter((doc) => doc.category === category),
	})).filter((group) => group.documents.length > 0);

/**
 * Groups documents into preferred language → other language → unassigned,
 * with category subgroups (Terms, Brochure, Form) inside each language section.
 * Empty language/category sections are omitted.
 */
export const groupDocumentsByLanguage = (
	documents: Document[],
	preferredLanguage: DocumentLanguage
): DocumentLanguageSection[] => {
	const buckets: Record<DocumentLanguageSectionKey, Document[]> = {
		en: [],
		"nb-NO": [],
		unassigned: [],
	};

	for (const doc of documents) {
		if (isDocumentLanguage(doc.language)) {
			buckets[doc.language].push(doc);
		} else {
			buckets.unassigned.push(doc);
		}
	}

	return getLanguageSectionOrder(preferredLanguage)
		.map((key) => {
			const sectionDocs = buckets[key];
			if (sectionDocs.length === 0) return null;

			return {
				key,
				language: key === "unassigned" ? null : key,
				categories: groupByCategory(sectionDocs),
			} satisfies DocumentLanguageSection;
		})
		.filter((section): section is DocumentLanguageSection => section !== null);
};

export const documentLanguageSectionTitleKey = (
	key: DocumentLanguageSectionKey
): string => {
	if (key === "en") return "calculator.documents.sections.en";
	if (key === "nb-NO") return "calculator.documents.sections.no";
	return "calculator.documents.sections.unassigned";
};
